/**
 * Contact Page — GE Lines Pte Ltd
 */

import { useState } from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useStorefront } from '@/context/StorefrontContext';
import { API_BASE } from '@/lib/api-base';
import { useSurfaceAccess } from '@/context/SurfaceAccessContext';

export function ContactPage() {
  const { brand, host } = useStorefront();
  const { ensureAccess, buildHeaders, surfaceUser } = useSurfaceAccess();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="max-w-5xl mx-auto px-4 py-16 animate-fade-in">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-3">Contact {brand.brandShortName}</h1>
        <p className="text-slate-500 max-w-lg mx-auto">
          Our operations team is available to assist with quotes, bookings, and enquiries.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Contact info */}
        <div className="space-y-6">
          {[
            {
              icon: Phone,
              title: 'Phone',
              lines: [brand.primaryPhone, brand.secondaryPhone].filter(Boolean),
            },
            {
              icon: Mail,
              title: 'Email',
              lines: [brand.supportEmail, brand.primaryEmail],
            },
            {
              icon: MapPin,
              title: 'Office',
              lines: brand.addressLines,
            },
            {
              icon: Clock,
              title: 'Operating Hours',
              lines: brand.officeHours,
            },
          ].map(item => (
            <div key={item.title} className="flex gap-4">
              <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                <item.icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-0.5">{item.title}</h3>
                {item.lines.map(l => (
                  <p key={l} className="text-sm text-slate-500">{l}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Enquiry form */}
        <div className="bg-white rounded-2xl shadow p-8 border border-slate-100">
          <h2 className="font-semibold text-slate-900 mb-5">Send an Enquiry</h2>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              setStatus('submitting');
              setError(null);
              try {
                const allowed = await ensureAccess('inquiry');
                if (!allowed) {
                  setStatus('idle');
                  return;
                }
                const subject = String(formData.get('subject') ?? '');
                const requestType = subject === 'Service Complaint' ? 'complaint' : 'inquiry';
                const payload = {
                  contactName: String(formData.get('contactName') ?? surfaceUser?.name ?? ''),
                  company: String(formData.get('company') ?? surfaceUser?.company ?? ''),
                  contactEmail: String(formData.get('contactEmail') ?? surfaceUser?.email ?? ''),
                  subject,
                  remarks: String(formData.get('remarks') ?? ''),
                };
                const res = await fetch(`${API_BASE}/api/selfservice/surface/requests`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...buildHeaders(),
                  },
                  body: JSON.stringify({
                    host,
                    requestType,
                    title: payload.subject || 'Other Inquiry',
                    message: payload.remarks,
                    details: payload,
                  }),
                });
                const body = await res.json() as { ok: boolean; error?: { message?: string } };
                if (!res.ok || !body.ok) throw new Error(body.error?.message ?? 'Unable to submit enquiry');
                setStatus('success');
                (e.currentTarget as HTMLFormElement).reset();
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Unable to submit enquiry');
                setStatus('idle');
              }
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                <input
                  name="contactName"
                  required
                  type="text"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-brand-400 focus:outline-none"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
                <input
                  name="company"
                  type="text"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-brand-400 focus:outline-none"
                  placeholder="Company name"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
              <input
                name="contactEmail"
                required
                type="email"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-brand-400 focus:outline-none"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Subject</label>
              <select name="subject" className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-brand-400 focus:outline-none bg-white">
                <option>Freight Quote</option>
                <option>Booking Enquiry</option>
                <option>Customer Portal Access</option>
                <option>Invoice / Payment</option>
                <option>Service Complaint</option>
                <option>Other</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Message</label>
              <textarea
                name="remarks"
                required
                rows={4}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-brand-400 focus:outline-none resize-none"
                placeholder="Tell us how we can help…"
              />
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {status === 'success' && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                Thank you. Our team will contact you shortly.
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full py-3 bg-brand-700 text-white font-medium rounded-lg hover:bg-brand-800 transition-colors"
            >
              {status === 'submitting' ? 'Sending...' : 'Send Enquiry'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
